package org.matthewo.pages.team.tabs

import emotion.react.css
import io.ktor.client.call.*
import io.ktor.client.plugins.resources.*
import js.coroutines.awaitCancellation
import kotlinx.coroutines.launch
import org.matthewo.YEAR_PARAM
import org.matthewo.api.MetricsAPI
import org.matthewo.api.MetricsIntoTheDeepCRIAPI
import org.matthewo.api.TeamData
import org.matthewo.api.castOrNull
import org.matthewo.client
import org.matthewo.hooks.useCoroutineScope
import org.matthewo.js.toFixed
import org.matthewo.math.ifNaN
import org.matthewo.pages.event.EVENT_CODE_PARAM
import org.matthewo.pages.team.TEAM_PARAM
import org.matthewo.router.div
import org.matthewo.routes.API
import org.matthewo.useLoading
import org.matthewo.useTitleHeader
import react.FC
import react.Props
import react.dom.html.ReactHTML.div
import react.useEffect
import react.useState
import tanstack.react.router.useParams
import tanstack.router.core.RoutePath
import web.cssom.*

val INTO_THE_DEEP_CRI_PLACEMENT_VISUALIZATION_PATH = RoutePath(TEAM_PATH / "ittdcri" / EVENT_CODE_PARAM)

val IntoTheDeepCRIPlacementVisualization = FC("IntoTheDeepCRIPlacementVisualization") {
    val coroutineScope = useCoroutineScope()
    val pathParams = useParams()

    var metrics by useState<TeamData<MetricsIntoTheDeepCRIAPI<Short>>>()

    val year = pathParams[YEAR_PARAM] ?: ""
    val teamNumber = pathParams[TEAM_PARAM] ?: ""
    val code = pathParams[EVENT_CODE_PARAM] ?: ""

    useEffect(pathParams) {
        val year = year.toIntOrNull() ?: return@useEffect
        val teamNumber = teamNumber.toIntOrNull() ?: return@useEffect
        val job = coroutineScope.launch {
            val response = client.get(API.Team.Metrics(year, teamNumber, code))
            metrics = response.body<TeamData<MetricsAPI<Short>>>().castOrNull()
        }
        awaitCancellation { job.cancel() }
    }

    useLoading(null == metrics)
    useTitleHeader(metrics) { metrics?.run { "$number - $name" } ?: "" }

    div {
        css {
            width = 100.pct
            height = 100.pct

            display = Display.grid
            gridTemplateColumns = array(1.fr, 1.fr)
            gridTemplateRows = array(2.5.em, 1.fr)
        }

        ["Auto", "Teleop"].forEach { text ->
            div {
                css {
                    fontSize = 2.em
                    textAlign = TextAlign.center
                }

                +text
            }
        }

        if (null == metrics) return@div

        VerticalVisualization {
            scale = 2.0
            this.metrics = metrics?.data?.let { m ->
                [
                    m.apSpecimenHigh1,
                    m.apSpecimenLow1,
                    m.apSpecimenHigh2,
                    m.apSpecimenLow2,
                    m.apSpecimenHigh3,
                    m.apSpecimenLow3,
                    m.apSpecimenHigh4,
                    m.apSpecimenLow4,
                ]
            }
        }
        VerticalVisualization {
            scale = 1.0
            this.metrics = metrics?.data?.let { m ->
                [
                    m.tpSpecimenHigh1,
                    m.tpSpecimenLow1,
                    m.tpSpecimenHigh2,
                    m.tpSpecimenLow2,
                    m.tpSpecimenHigh3,
                    m.tpSpecimenLow3,
                    m.tpSpecimenHigh4,
                    m.tpSpecimenLow4,
                ]
            }
        }
    }
}

private external interface VisualizationProps : Props {
    var metrics: List<Short>?
    var scale: Double
}

private val VerticalVisualization = FC<VisualizationProps>("VerticalVisualization") { props ->
    val metrics = props.metrics
    div {
        css {
            display = Display.flex
            flexDirection = FlexDirection.column
        }

        repeat(4) { i ->
            div {
                css {
                    flexGrow = number(1.0)

                    display = Display.flex
                    flexDirection = FlexDirection.column
                    justifyContent = JustifyContent.spaceEvenly
                    padding = min(20.px, 10.pct)
                }

                ["High", "Low"].forEachIndexed { j, h ->
                    val metric = if (null == metrics) Double.NaN else metrics[2 * i + j] / props.scale / (if (j == 0) 10.0 else 6.0)

                    div {
                        css {
                            display = Display.flex
                            flexDirection = FlexDirection.column
                            height = 2 * 1.5.em + 10.px
                        }

                        div {
                            css {
                                height = 1.5.em
                                display = Display.grid
                                gridTemplateColumns = repeat(3, 1.fr)
                            }

                            div {
                                css {
                                    textAlign = TextAlign.start
                                }
                                +"${i + 1} $h"
                            }

                            div {
                                css {
                                    textAlign = TextAlign.center
                                }
                                if (!metric.isNaN()) +metric.toFixed(1)
                            }
                        }

                        div {
                            css {
                                height = 10.px
                                background = NamedColor.yellow

                                display = Display.flex
                                justifyContent = JustifyContent.center
                            }

                            div {
                                css {
                                    width = (metric.ifNaN(0.0).coerceIn(0.0..15.0) / 0.15).pct
                                    height = 100.pct
                                    background = NamedColor.black
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
